import React, { useEffect } from "react";
import UI from "@mile-meeting/mile-ui";
import {
  TopFilterContainer,
  TopFilterGroup,
  Container,
  PeriodChipContainer,
  MeetingRoomLabelContainer,
} from "./MeetingRoomStatusDashboardFilter.styles";
import CalendarFilter from "pages/meeting/NoshowPolicyManagement/NoshowReservationManagementTab/uis/calendar";
import { getKorDate } from "utils/date";
import ToolTip from "uis/new-tootip";
import MeetingRoomFilter from "./MeetingRoomFilter/MeetingRoomFilter";
import { getMeetingRoomOfMonthlyData } from "apis/analytics/meeting/dashboard";
import { useToast } from "hook/useToast";
import { set } from "lodash";

type MeetingRoomStatusDashboardFilterProps = {
  onFilterChange?: (data: { startDate: Date; endDate: Date; selectedRoom?: string }) => void;
};
export const MeetingRoomStatusDashboardFilter: React.FC<MeetingRoomStatusDashboardFilterProps> = ({
  onFilterChange = () => {},
}) => {
  const [openCalendarModal, setOpenCalendarModal] = React.useState<boolean>(false);
  const calendarRef = React.useRef<HTMLDivElement>(null);
  const [selectedRoom, setSelectedRoom] = React.useState<string | undefined>(undefined);
  const [period, setPeriod] = React.useState<{ startDate: Date; endDate: Date }>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1), // 이번 달의 첫 날
    endDate: new Date(new Date().setDate(new Date().getDate())), // 어제로 설정
  });
  const { showToast } = useToast();
  const [meetingRoomList, setMeetingRoomList] = React.useState<string[]>([]);

  const isDateDifferenceExceedingMax = (start: Date, end: Date, maxDurationInMonths: number): boolean => {
    const yearDifference = end.getFullYear() - start.getFullYear();
    const monthDifference = end.getMonth() - start.getMonth();

    const totalMonthsDifference = yearDifference * 12 + monthDifference;

    return totalMonthsDifference > maxDurationInMonths;
  };

  //* 해당 조회 기간에 맞는 회의실 리스트 조회
  const fetchMeetingRoomList = async (startDate: Date, endDate: Date) => {
    const res = await getMeetingRoomOfMonthlyData({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
    });

    if (res.success) {
      setMeetingRoomList(res.data ?? []);
      if (selectedRoom && !res.data?.includes(selectedRoom)) setSelectedRoom(undefined);
    }
  };

  //* 조회 기간 칩을 클릭했을 때의 핸들러
  const onHandlePeriodChip = (type: "current" | "previous") => {
    let settingStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    let settingEndDate = new Date(new Date().setDate(new Date().getDate()));
    if (type === "current") {
      settingStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1); // 이번 달의 첫 날
      settingEndDate = new Date(new Date().setDate(new Date().getDate()));
    } else if (type === "previous") {
      const previousMonth = new Date();
      previousMonth.setMonth(previousMonth.getMonth() - 1);
      settingStartDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1); // 이전 달의 첫 날
      settingEndDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0); // 이전 달의 마지막 날
    }
    setPeriod({
      startDate: settingStartDate,
      endDate: settingEndDate,
    });
    onFilterChange({
      startDate: settingStartDate,
      endDate: settingEndDate,
      selectedRoom,
    });
    setSelectedRoom(undefined);
    setOpenCalendarModal(false);
    fetchMeetingRoomList(settingStartDate, settingEndDate);
  };

  useEffect(() => {
    fetchMeetingRoomList(period.startDate, period.endDate);
  }, []);

  useEffect(() => {
    const handler = (event: any) => {
      if (openCalendarModal && calendarRef.current && !calendarRef.current.contains(event.target)) {
        setOpenCalendarModal?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [openCalendarModal]);

  return (
    <Container className="meeting-room-status-dashboard-filter">
      <TopFilterContainer>
        <TopFilterGroup ref={calendarRef} className="period-filter">
          <UI.Typography type="body01 normal" color="var(--alias-color-label-normal)">
            조회 기간
          </UI.Typography>
          <div onFocus={() => setOpenCalendarModal(true)}>
            <UI.InputGroup.TextField
              width={160}
              value={getKorDate(period.startDate.toDateString()).replaceAll(".", ". ")}
              size="small"
              prefixIcon="calendar"
              showPrefixIcon
              disabledFocus
            />
          </div>
          <div style={{ width: 12, height: 1, backgroundColor: "var(--alias-color-label-normal)" }} />
          <div onFocus={() => setOpenCalendarModal(true)}>
            <UI.InputGroup.TextField
              width={160}
              value={getKorDate(period.endDate.toDateString()).replaceAll(".", ". ")}
              size="small"
              prefixIcon="calendar"
              showPrefixIcon
              disabledFocus
            />
          </div>
          <CalendarFilter
            selectedDate={[period.startDate, period.endDate]}
            open={openCalendarModal}
            onChange={async (startDate, endDate) => {
              if (startDate && endDate) {
                setPeriod({ startDate, endDate });
                await fetchMeetingRoomList(startDate, endDate);
                onFilterChange({ startDate, endDate, selectedRoom });
              }
              if (isDateDifferenceExceedingMax(startDate, endDate, 6)) {
                showToast("최대 조회 가능 기간을 초과했습니다. 기간을 다시 선택해 주세요.", "warning");
                setPeriod({
                  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                  endDate: new Date(new Date().setDate(new Date().getDate())),
                });
                await fetchMeetingRoomList(
                  new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                  new Date(new Date().setDate(new Date().getDate()))
                );
                onFilterChange({
                  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                  endDate: new Date(new Date().setDate(new Date().getDate())),
                  selectedRoom,
                });
              } else {
                setPeriod({ startDate, endDate });
              }
            }}
          />
        </TopFilterGroup>
        <TopFilterGroup className="room-filter">
          <MeetingRoomLabelContainer>
            <UI.Typography type="body01 normal" color="var(--alias-color-label-normal)">
              회의실
            </UI.Typography>
            <ToolTip
              text="조회 기간 동안 이용된 회의실만 조회됩니다."
              icon="question"
              position={{
                top: 24,
                left: -40,
              }}
            />
          </MeetingRoomLabelContainer>
          <MeetingRoomFilter
            meetingRoomList={meetingRoomList}
            selectedRoom={selectedRoom}
            onChange={async (room) => {
              setSelectedRoom(room);
              onFilterChange({ startDate: period.startDate, endDate: period.endDate, selectedRoom: room });
            }}
          />
        </TopFilterGroup>
      </TopFilterContainer>
      <UI.Typography type="label01" color="var(--alias-color-label-alternative)">
        * 최대 조회 가능 기간은 6개월입니다.
      </UI.Typography>
      <PeriodChipContainer>
        <UI.Chip.Outlined
          type="primary"
          size="medium"
          title="이번 달"
          onClick={() => onHandlePeriodChip("current")}
          selected={
            //* 이번달 첫날부터 어제까지인지 확인
            period.startDate.getFullYear() === new Date().getFullYear() &&
            period.startDate.getMonth() === new Date().getMonth() &&
            period.startDate.getDate() === 1 &&
            period.endDate.getFullYear() === new Date().getFullYear() &&
            period.endDate.getMonth() === new Date().getMonth() &&
            period.endDate.getDate() === new Date().getDate()
          }
        />
        <UI.Chip.Outlined
          type="primary"
          size="medium"
          title="지난 달"
          onClick={() => onHandlePeriodChip("previous")}
          selected={
            //* 지난달 첫날부터 마지막 날까지인지 확인
            period.startDate.getFullYear() === new Date().getFullYear() &&
            period.startDate.getMonth() === new Date().getMonth() - 1 &&
            period.startDate.getDate() === 1 &&
            period.endDate.getFullYear() === new Date().getFullYear() &&
            period.endDate.getMonth() === new Date().getMonth() - 1 &&
            period.endDate.getDate() === new Date(new Date().getFullYear(), new Date().getMonth() - 1 + 1, 0).getDate()
          }
        />
      </PeriodChipContainer>
    </Container>
  );
};
export default React.memo(MeetingRoomStatusDashboardFilter);
